import React from 'react';
import { IOption } from '../../../../components/Select/Select';
import Select from '../../../../components/Select';
import { Mention, MentionsInput } from 'react-mentions';
import { SendSMSBaseOnQuestionContainer } from './styles';
import { IField, IFieldNotification } from '../../../../store/tasks/tasksType';
import Icon from '../../../../components/Icon';
import Input from '../../../../components/Input';
import { gray } from '../../../../utils/style/colors';
import i18n from '../../../../i18n';
import { ICompany } from '../../../../store/companies/companiesTypes';

interface IProps {
  item: IFieldNotification;
  id: number;
  handleDeleteField: () => void;
  onUpdateFieldNotification: (ind: number, obj: IFieldNotification) => void;
  questionFields: Array<IField>;
  notificationsPatternData: { display: string; id: string }[];
  company: ICompany;
}

const FieldNotification: React.FC<IProps> = ({
  item,
  id,
  handleDeleteField,
  onUpdateFieldNotification,
  questionFields,
  notificationsPatternData,
  company,
}) => {
  const fixedRolesOptions = [
    {
      label: i18n.t('assignee', { ns: 'companyManagement' }),
      value: 'assignee',
    },
    {
      label: i18n.t('followers', { ns: 'taskManager' }),
      value: 'followers',
    },
    {
      label: i18n.t('verifier', { ns: 'companyManagement' }),
      value: 'verifier',
    },
    {
      label: i18n.t('publisher', { ns: 'companyManagement' }),
      value: 'publisher',
    },
    {
      label: i18n.t('subtaskPublisher', { ns: 'companyManagement' }),
      value: 'subtaskPublisher',
    },
  ];
  const roles = Object.values(company.roles).reduce(
    (acc: any[], role) => [...acc, { id: role.id, name: role.name }],
    []
  );

  const fixedRoles = roles.reduce(
    (acc: any, role) => [...acc, { label: role.name, value: `R${role.id}` }],
    []
  );
  const roleOrUserListOptions = [...fixedRolesOptions, ...fixedRoles];
  const savedTargetUser = item.targetUser;

  const selectedQuestionOptions = questionFields
    .filter((item) => Object.values(item.options || {}).length > 0)
    .map((item) => ({
      label: item.title!,
      value: item.name!,
    }));

  const specificQuestionOptions = Object.values(
    questionFields.find((it) => it.name === item.fieldName)?.options || {}
  ).map((it) => ({ value: it, label: it }));

  const handleSelectedRoles = (values: IOption[]) => {
    let userId =
      fixedRolesOptions.find((item) => item.value === values[0].value)?.value ??
      roles.find((item) => `R${item.id}` === values[0].value).id;
    if (typeof userId === 'number') {
      userId = `R${userId}`;
    }
    onUpdateFieldNotification(id, {
      ...item,
      targetUser: userId,
    });
  };

  const handleSelectedQuestion = (values: IOption[]) => {
    onUpdateFieldNotification(id, {
      ...item,
      fieldName: values[0].value.toString(),
      values: [],
    });
  };

  const handleSelectedQuestionOptions = (values: IOption[]) => {
    onUpdateFieldNotification(id, {
      ...item,
      values: values.map((item) => item.value.toString()) || [],
    });
  };

  const handleUpdateFieldNotification = (
    fieldNotification: Partial<IFieldNotification>
  ) => {
    onUpdateFieldNotification(id, {
      ...item,
      ...fieldNotification,
    });
  };

  return (
    <SendSMSBaseOnQuestionContainer>
      <div
        style={{
          marginTop: '1rem',
          cursor: 'pointer',
        }}
      >
        <Icon
          type="minus-circle"
          style={{ color: `${gray}` }}
          onClick={() => handleDeleteField()}
        ></Icon>
      </div>
      <Select
        label={i18n.t('sendTo', { ns: 'companyManagement' }) as string}
        options={roleOrUserListOptions}
        values={
          savedTargetUser
            ? [
                {
                  label: savedTargetUser.startsWith('R')
                    ? roles.find(
                        (role) => role.id === +savedTargetUser.slice(1)
                      )?.name
                    : (fixedRolesOptions.find(
                        (role) => role.value === savedTargetUser
                      )?.label as string),
                  value: item.targetUser,
                },
              ]
            : []
        }
        allowRemove={false}
        multiChoice={false}
        onChangeValues={handleSelectedRoles}
        inputStyle={{ minWidth: '6rem' }}
        isRequired={true}
      />
      <Select
        label={i18n.t('question', { ns: 'companyManagement' }) as string}
        options={selectedQuestionOptions}
        values={selectedQuestionOptions.filter(
          (it) => item.fieldName === it.value
        )}
        allowRemove={false}
        multiChoice={false}
        onChangeValues={handleSelectedQuestion}
        inputStyle={{ minWidth: '6rem' }}
        isRequired={true}
      />
      <Select
        label={i18n.t('whichOptions', { ns: 'companyManagement' }) as string}
        options={specificQuestionOptions}
        values={specificQuestionOptions.filter((it) => {
          return item.values.includes(it.value.toString());
        })}
        allowRemove={true}
        multiChoice={true}
        isRequired={true}
        onChangeValues={handleSelectedQuestionOptions}
      />
      <div style={{ marginTop: '1rem' }}>
        <Input
          id={'sms_' + id.toString()}
          type="checkbox"
          checked={item.notifyWithSMS}
          label={i18n.t('notifyWithSMS', { ns: 'companyManagement' }) as string}
          onChange={() =>
            handleUpdateFieldNotification({
              notifyWithSMS: !item.notifyWithSMS,
            })
          }
        />
      </div>
      <div style={{ marginTop: '1rem' }}>
        <Input
          id={'draft_' + id.toString()}
          type="checkbox"
          checked={item.notifyWhenDraft}
          label={
            i18n.t('notifyWhenDraft', { ns: 'companyManagement' }) as string
          }
          onChange={() =>
            handleUpdateFieldNotification({
              notifyWhenDraft: !item.notifyWhenDraft,
            })
          }
        />
      </div>
      <div
        style={{
          flex: '1',
          height: '7rem',
          padding: '1rem 0',
          marginBottom: '1rem',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <label>
          {i18n.t('messagePattern', { ns: 'companyManagement' }) as string}
        </label>
        <MentionsInput
          value={item.message}
          onChange={(e) => {
            handleUpdateFieldNotification({
              message: e.target.value,
            });
          }}
          forceSuggestionsAboveCursor={true}
          allowSuggestionsAboveCursor={true}
          allowSpaceInQuery={true}
          className="surveySMSPattern"
          placeholder={
            i18n.t('messagePattern', { ns: 'companyManagement' }) as string
          }
          style={{ zIndex: 100, minWidth: '250px' }}
          required={true}
        >
          <Mention
            trigger={'%'}
            data={notificationsPatternData}
            markup={'%__id__'}
            displayTransform={(id: string) =>
              '(' +
              (notificationsPatternData.find((it) => it.id === id)?.display ||
                id) +
              ')'
            }
            regex={/%(\S+)/}
            appendSpaceOnAdd={true}
          />
          <Mention
            trigger={'٪'}
            data={notificationsPatternData}
            markup={'%__id__'}
            displayTransform={(id: string) =>
              '(' +
              (notificationsPatternData.find((it) => it.id === id)?.display ||
                id) +
              ')'
            }
            regex={/%(\S+)/}
            appendSpaceOnAdd={true}
          />
        </MentionsInput>
      </div>
    </SendSMSBaseOnQuestionContainer>
  );
};

export default FieldNotification;
